import { type Editor, isNodeSelection, type JSONContent, posToDOMRect } from "@tiptap/core";
import Link from "@tiptap/extension-link";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import memoizeOne from "memoize-one";
import moment, { type Moment } from "moment";
import { useContext, useEffect, useRef } from "react";

import { EnvironmentInfoContext } from "helpers/globalContext/EnvironmentInfoContext";

import { EntityTypes } from "types/enum";

import { isNullOrWhitespace } from "utilities/string/string";

import {
    type EditorControlBreakpointNames,
    editorControlBreakpoints,
} from "commonComponents/btWrappers/editor/core/EditorControlBar/EditorControlBar.types";
import Emoji from "commonComponents/btWrappers/editor/core/extensions/custom/emoji/EmojiExtension";
import EntityLinkExtension from "commonComponents/btWrappers/editor/core/extensions/custom/entityLink/EntityLinkExtension";
import ImageExtension from "commonComponents/btWrappers/editor/core/extensions/custom/image/ImageExtension";
import IndentParagraphExtension from "commonComponents/btWrappers/editor/core/extensions/custom/IndentParagraphExtension";
import { type EditorControlMode } from "commonComponents/btWrappers/editor/core/extensions/custom/OverrideTopLevelEnterKeyExtension";
import TitleExtension from "commonComponents/btWrappers/editor/core/extensions/custom/TitleExtension";
import { type IEditor } from "commonComponents/btWrappers/editor/core/useBTEditor.types";
import { type EditorContent } from "commonComponents/btWrappers/editor/editor.types";
import { RelatedItemType } from "commonComponents/entity/relatedItem/RelatedItem.types";

export const supportedEditorImageTypes = {
    extensions: new Set([
        ".png",
        ".jpg",
        ".jpeg",
        ".gif",
        ".bmp",
        ".svg",
        ".webp",
        ".heic",
        ".heif",
    ]),
    mimeTypes: new Set([
        "image/png",
        "image/jpeg",
        "image/gif",
        "image/bmp",
        "image/svg+xml",
        "image/webp",
    ]),
};

/**
 * Data attribute used to identify the editor container element. Used to detect if focus
 * is within the editor ecosystem (controls, overlays, etc.)
 */
export const EDITOR_CONTAINER_ATTRIBUTE = "data-bt-editor-container";

/**
 * Checks if a given element is within an editor container.
 * Traverses up the DOM tree from the element to check for the editor container attribute.
 * @param element The element to check (typically event.relatedTarget from a blur event)
 * @returns true if element is within an editor, false otherwise
 */
export function isElementWithinEditor(element: EventTarget | Element | null): boolean {
    if (!element || !(element instanceof Element)) {
        return false;
    }

    let currentElement: Element | null = element;
    while (currentElement) {
        if (currentElement.hasAttribute(EDITOR_CONTAINER_ATTRIBUTE)) {
            return true;
        }
        currentElement = currentElement.parentElement;
    }
    return false;
}

export const isAlignmentActive = (editor: Editor, align: "left" | "center" | "right") => {
    return editor.isActive({ textAlign: align });
};

export const isFontSizeActive = (editor: Editor, fontSize: "small" | "medium" | "large") => {
    return fontSize === "medium"
        ? !("fontSize" in editor.getAttributes("textStyle"))
        : editor.isActive("textStyle", { fontSize });
};

export const isAlignmentEnabled = (editor: Editor, align: "left" | "center" | "right") => {
    return (
        !isSelectionExclusivelyInTitle(editor, editor.state.selection) &&
        editor.can().chain().focus().setTextAlign(align).run()
    );
};

export const getReactEditorStateFromTiptapEditor: (
    editor: Editor,
) => EditorControlMode | undefined = (editor: Editor) => {
    return editor.storage["editorState"]?.mode;
};

export const toggleEditorMode: (editor: IEditor, newMode: EditorControlMode) => void = (
    editor: IEditor,
    newMode: EditorControlMode,
) => {
    editor.editorStateDispatch({ type: "setControlBarMode", payload: newMode });
};

export const getEditorControlBreakpointFromWidth = (
    width: number,
): EditorControlBreakpointNames => {
    return (
        editorControlBreakpoints.find(
            ([breakPointStart, breakPointEnd]) =>
                width >= breakPointStart && (!breakPointEnd || width <= breakPointEnd),
        )?.[2] ?? "lg"
    );
};

export const addNewParagraph = (editor?: Editor) => {
    editor?.commands.first(({ commands }) => [
        () => commands.newlineInCode(),
        () => commands.createParagraphNear(),
        () => commands.liftEmptyBlock(),
        () => commands.splitBlock(),
    ]);
};
export const addBreak = (editor?: Editor) => {
    editor?.commands.setHardBreak();
};

export const useFrequentEditorRemountSafeguard = (value: EditorContent | null) => {
    const lastTimeSinceValueChange = useRef<Moment | null>(null);
    const envInfo = useContext(EnvironmentInfoContext);
    const apiEditorContentUuid = value?._uuid;
    const isProduction = envInfo?.isProduction;
    useEffect(() => {
        if (!isProduction && lastTimeSinceValueChange.current) {
            const currentMoment = moment();
            const millisecondsPastSinceChange = lastTimeSinceValueChange.current.diff(
                currentMoment,
                "milliseconds",
            );

            if (millisecondsPastSinceChange <= 500) {
                throw new Error(
                    `Dev error: Editor values changed too frequently causing rerenders.
The editor's initial value should only be changed when an API call is made.
If it's changed too frequently, it will trigger many rerenders, impacting performance. Either too
many API calls were made that changed the editor content, or EditorContent is being reinstanced
unnecessarally every render. Otherwise, this is a false positive and if the usecase is valid we can
remove this safety check.
`,
                );
            }

            lastTimeSinceValueChange.current = currentMoment;
        }
    }, [apiEditorContentUuid, isProduction]);
};

/**
 * This higher order function is useful for seeing if a node is within the current text selection.
 * This is useful so that nodes can check if they are selected without running nodesBetween
 * excessively
 */
export const fastIsNodeInSelection = memoizeOne(
    (doc: Editor["state"]["doc"], selection: Editor["state"]["selection"]) => {
        const nodesInSelection = new Set<typeof doc>();
        doc.nodesBetween(selection.from, selection.to, (node: typeof doc) => {
            nodesInSelection.add(node);
        });

        return (node: typeof doc) => {
            return nodesInSelection.has(node);
        };
    },
);

/**
 * This is a temporary function that shouldn't be needed when TipTap fixes issues with the `can()` functions.
 * Basically, calling `can()` for editor functions returns true for a lot of commands even if the command
 * is not possible to actually run (should be fixed for most cases in https://github.com/ueberdosis/tiptap/pull/3223
 * and https://github.com/ueberdosis/tiptap/issues/3229 **which are different issues**)
 */
export const isSelectionExclusivelyInTitle = memoizeOne(
    (editor: Editor, selection: Editor["state"]["selection"]) => {
        const { $from, $to } = selection;
        if (isNodeSelection(selection)) {
            const { node } = selection;

            return node.type.name === TitleExtension.name;
        }

        let isSelectionExclusivelyInTitle = true;
        editor.state.doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
            const selectionEndsAtNodeBeginning = $to.pos - 1 > pos || $from.pos === $to.pos;
            const isNotTitleNode = node.type.name !== TitleExtension.name;
            if (selectionEndsAtNodeBeginning && isNotTitleNode) {
                isSelectionExclusivelyInTitle = false;
            }

            return false;
        });

        return isSelectionExclusivelyInTitle;
    },
);

export function hasSelection(editor: Editor) {
    const { from, to } = editor.view.state.selection;
    return from !== to;
}

export function getNodeAtCursor(editor: Editor) {
    const { from } = editor.view.state.selection;
    return editor.state.doc.nodeAt(from);
}

export function isSelectionLinkable(editor: Editor) {
    if (!hasSelection(editor)) {
        return true;
    }

    const { view, state } = editor;
    const { from, to } = view.state.selection;

    let hasSingleTextBlockWithinSelection = false;
    let paragraphCounts = 0;
    state.doc.nodesBetween(from, to, (node) => {
        if (node.type.name === Paragraph.name) {
            paragraphCounts++;
        }
        hasSingleTextBlockWithinSelection = paragraphCounts === 1;

        // Stop recursing (return false) if we know we've hit more than one paragraphs
        return paragraphCounts < 1;
    });

    const hasExistingLinkWithinSelection = state.doc.rangeHasMark(
        from,
        to,
        state.schema.marks.link,
    );

    return hasSingleTextBlockWithinSelection && !hasExistingLinkWithinSelection;
}

export function areMultipleLinksSelected(editor: Editor) {
    const { view, state } = editor;
    const { from, to } = view.state.selection;

    const links = [];
    state.doc.nodesBetween(from, to, (node) => {
        if (node.isLeaf) {
            links.push(node.marks.filter((m) => m.type.name === Link.name));
        }
    });

    return links.length > 1;
}

export function getLinkAtCursor(editor: Editor) {
    const nodeAtCursor = getNodeAtCursor(editor);
    if (!nodeAtCursor) {
        return null;
    }

    const mark = nodeAtCursor.marks.find((m) => m.type.name === Link.name);
    return (mark?.attrs?.href as string) ?? null;
}

export function getTextAtCursor(editor: Editor) {
    const nodeAtCursor = getNodeAtCursor(editor);
    if (!nodeAtCursor) {
        return null;
    }

    return nodeAtCursor.textContent;
}

export function getSelectedText(editor: Editor) {
    if (!hasSelection(editor)) {
        return null;
    }

    const { selection } = editor.view.state;
    const { from, to } = selection;
    return editor.state.doc.textBetween(from, to, " ");
}

export function getPositionOfNodeAtCursor(editor: Editor) {
    const node = getNodeAtCursor(editor);
    if (!node) {
        return null;
    }

    const { selection } = editor.view.state;
    const { $from } = selection;

    const from = $from.pos - $from.textOffset;
    const to = from + node.textContent.length;

    return { from, to };
}

export function getDOMPositionOfSelection(editor: Editor) {
    const { view } = editor;
    const { state } = view;
    const { selection } = state;

    // support for CellSelections
    const { ranges } = selection;
    const from = Math.min(...ranges.map((range) => range.$from.pos));
    const to = Math.max(...ranges.map((range) => range.$to.pos));

    if (isNodeSelection(state.selection)) {
        const node = view.nodeDOM(from) as HTMLElement;

        if (node) {
            return node.getBoundingClientRect();
        }
    }
    return posToDOMRect(view, from, to);
}

export function canOpenLinkEdit(editor: Editor) {
    return (
        (!editor.isActive("link") && isSelectionLinkable(editor)) ||
        (editor.isActive("link") && !areMultipleLinksSelected(editor))
    );
}

export function getIndentationFromParagraphNode(node: HTMLElement) {
    let rawMarginLeft: number | null = null;
    let rawTextIndent: number | null = null;
    if (node.hasAttribute("style")) {
        if (
            !isNullOrWhitespace(node.style.marginLeft) &&
            /^((\d+)(pt|px))$/gm.exec(node.style.marginLeft.trim())
        ) {
            rawMarginLeft = parseInt(node.style.marginLeft);
        }

        if (
            !isNullOrWhitespace(node.style.textIndent) &&
            /^((\d+)(pt|px))$/gm.exec(node.style.textIndent.trim())
        ) {
            rawTextIndent = parseInt(node.style.textIndent);
        }
    }
    return { rawMarginLeft, rawTextIndent };
}

export function isParagraphNode(node: Editor["state"]["doc"]) {
    return node.type.name === Paragraph.name || node.type.name === IndentParagraphExtension.name;
}

export const entityTypeToRelatedItemType = {
    [EntityTypes.Todo]: RelatedItemType.ToDo,
    [EntityTypes.ChangeOrder]: RelatedItemType.ChangeOrder,
    [EntityTypes.PurchaseOrder]: RelatedItemType.PurchaseOrder,
    [EntityTypes.Bill]: RelatedItemType.Bill,
    [EntityTypes.Schedule]: RelatedItemType.ScheduleItem,
    [EntityTypes.Selection]: RelatedItemType.Selection,
    [EntityTypes.DailyLog]: RelatedItemType.DailyLog,
    [EntityTypes.Warranty]: RelatedItemType.Warranty,
    [EntityTypes.Rfi]: RelatedItemType.Rfi,
    [EntityTypes.Bid]: RelatedItemType.Bid,
    [EntityTypes.Estimate]: RelatedItemType.Estimate,
    [EntityTypes.Lead]: RelatedItemType.Lead,
    [EntityTypes.Job]: RelatedItemType.Jobsite,
    [EntityTypes.BidPackage]: RelatedItemType.BidPackage,
    [EntityTypes.LeadProposal]: RelatedItemType.LeadProposal,
    [EntityTypes.SelectionChoice]: RelatedItemType.SelectionChoice,
    [EntityTypes.Allowance]: RelatedItemType.Allowance,
    [EntityTypes.CostItem]: RelatedItemType.CostItem,
    [EntityTypes.CreditMemo]: RelatedItemType.CreditMemo,
    [EntityTypes.ClientUpdate]: RelatedItemType.ClientUpdate,
    [EntityTypes.Deposit]: RelatedItemType.Deposit,
    [EntityTypes.OwnerInvoice]: RelatedItemType.CustomerInvoice,
    [EntityTypes.Receipt]: RelatedItemType.Receipt,
    [EntityTypes.Task]: RelatedItemType.Task,
};

/**
 * Delete from the editor without adding to the history.
 * Be VERY careful when using this, as any content deleted this way will not be recoverable!
 */
export function deleteNoHistory(editor: Editor, from: number, to: number) {
    let tr = editor.state.tr;
    tr = tr.deleteRange(from, to);
    tr.setMeta("addToHistory", false);
    editor.view.dispatch(tr);
}

/**
 * Updates a node's attributes at position pos without adding to the transaction history.
 * Be VERY careful when using this, as any attributes deleted this way will not be recoverable!
 */
export function updateAttributesNoHistory(
    editor: Editor,
    pos: number,
    attributes: Record<string, any>,
) {
    let tr = editor.state.tr;
    tr = tr.setNodeMarkup(pos, undefined, attributes);
    tr.setMeta("addToHistory", false);
    editor.view.dispatch(tr);
}

/**
 * Compare semver strings a to b
 * Allows for partial versions (e.g. "1" is equivalent to "1.0.0")
 * Doesn't allow for non-numeric characters in version strings (e.g. "1.0-beta" is invalid)
 * @param a version string
 * @param b version string
 * @returns -1 if less, 0 if equal, 1 if greater
 */
export function semverCompare(a: string, b: string) {
    const aParts = a.split(".");
    const bParts = b.split(".");
    const maxLength = Math.max(aParts.length, bParts.length);
    for (let i = 0; i < maxLength; i++) {
        const aPart = Number(aParts[i] || "0");
        const bPart = Number(bParts[i] || "0");
        if (isNaN(aPart) || isNaN(bPart)) {
            throw new Error(`Invalid semver string: ${a} or ${b}`);
        }
        if (aPart > bPart) {
            return 1;
        }
        if (aPart < bPart) {
            return -1;
        }
    }
    return 0;
}

function doesEditorNodeHaveContent(editorNode: JSONContent): boolean {
    switch (editorNode.type) {
        case Text.name:
            return (editorNode.text ?? "").trim().length > 0;
        case ImageExtension.name:
        case Emoji.name:
        case EntityLinkExtension.name:
            return true;
    }

    return editorNode.content?.some(doesEditorNodeHaveContent) ?? false;
}

/**
 * Checks if the passed editor content has any substantive content within it (text nodes with text, images, etc.)
 * @returns true if one or more nodes have "substantive" content, false otherwise
 */
export function doesEditorContentHaveContent(content: EditorContent) {
    if (!content.content) {
        return false;
    }

    if (typeof content.content === "string") {
        return content.content.trim().length > 0;
    }

    const jsonContentArray = Array.isArray(content.content) ? content.content : [content.content];
    return jsonContentArray.some(doesEditorNodeHaveContent);
}
